import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import CustomServerSideTable from "commons/table/CustomServerSideTable";
import ResetListState from "components/ResetListState";
import PERMISSIONS from "rbac/Permissions.json";
import { getLoggedInUser } from "services/AuthService";
import { showNotification } from "services/NotificationService";
import { getErrorMessage } from "services/UtilityService";
import { DEFAULT_PAGE_SIZE } from "./aiCallLogConstants";
import { buildAiCallLogQuery, fetchAiCallLogPage } from "./aiCallLogApi";
import createAiCallLogColumns from "./aiCallLogColumns";
import { parseFilteredToApiParams } from "./aiCallLogFilterUtils";
import {
  AI_CALL_LOG_TABLE_ROW_KEY,
  withAiCallLogTableRowKeys,
} from "./aiCallLogTableRows";

const AI_CALL_LOG_PAGE_KEY = "AI_CALL_LOG_PAGE_SIZE";

const INITIAL_FILTERED = [];

function userHasAiCallLogAccess() {
  const user = getLoggedInUser();
  const a = user?.authorities || [];
  return a.includes(PERMISSIONS.PERM_PHI_ACCESS);
}

const AiCallLogList = () => {
  const allowed = useMemo(() => userHasAiCallLogAccess(), []);
  const columns = useMemo(() => createAiCallLogColumns(), []);

  const [listState, setListState] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    sorted: [],
    filtered: INITIAL_FILTERED,
    resized: [],
    expanded: {},
  });

  const [rawRows, setRawRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const fetchGenerationRef = useRef(0);

  const tableRows = useMemo(() => withAiCallLogTableRowKeys(rawRows), [rawRows]);

  const onUpdateTable = useCallback((filterValues, sortValues, pageSize, pageNo) => {
    const filtered = filterValues || [];
    setListState((prev) => ({
      ...prev,
      filtered,
      sorted: sortValues || [],
      pageSize,
      page: pageNo,
    }));

    const apiParams = parseFilteredToApiParams(filtered);
    const body = buildAiCallLogQuery({
      ...apiParams,
      limit: pageSize,
      offset: pageNo * pageSize,
    });

    fetchGenerationRef.current += 1;
    const generation = fetchGenerationRef.current;

    setLoading(true);
    fetchAiCallLogPage(body)
      .then((data) => {
        if (generation !== fetchGenerationRef.current) return;
        setRawRows(data.records || []);
        setTotalCount(typeof data.totalElements === "number" ? data.totalElements : 0);
      })
      .catch((e) => {
        if (generation !== fetchGenerationRef.current) return;
        setRawRows([]);
        setTotalCount(0);
        showNotification({
          title: "Error loading AI call log",
          message: getErrorMessage(e?.data) || getErrorMessage("SOMETHING_WENT_WRONG"),
          position: "bl",
          type: "error",
        });
      })
      .finally(() => {
        if (generation !== fetchGenerationRef.current) return;
        setLoading(false);
      });
  }, []);

  const resetList = useCallback(() => {
    onUpdateTable(INITIAL_FILTERED, [], listState.pageSize, 0);
  }, [onUpdateTable, listState.pageSize]);

  /** Top-align cells that stack multiple lines so content doesn’t sit vertically centered. */
  const callLogTdStyle = useCallback((_row, cell) => {
    const id = cell?.column?.id;
    if (
      id === "switchInfoIds" ||
      id === "switchStatuses" ||
      id === "outcomeTags"
    ) {
      return { verticalAlign: "top" };
    }
    return {};
  }, []);

  /**
   * CustomServerSideTable only invokes onUpdateTableData when filters/sort diverge from props
   * or when pagination changes. On first paint those match defaults and pagination is unchanged,
   * so no request runs. Call Group avoids this by loading filter config from the API (props change).
   */
  useEffect(() => {
    if (!allowed) return;
    onUpdateTable(INITIAL_FILTERED, [], DEFAULT_PAGE_SIZE, 0);
  }, [allowed, onUpdateTable]);

  if (!allowed) {
    return (
      <Container fluid className="list-container">
        <p>You do not have access to the AI Call Log.</p>
      </Container>
    );
  }

  return (
    <Container fluid className="list-container">
      <Row>
        <Col md={12}>
          <div
            className="codex-version-display-bar"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <span>
              Total records:{" "}
              <strong>{loading && totalCount === 0 ? "…" : totalCount}</strong>
            </span>
            <strong>AI Call Log</strong>
            <div style={{ display: "flex", alignItems: "center" }}>
              <ResetListState onReset={resetList} resetterDisabled={loading} />
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <CustomServerSideTable
            columnDefination={columns}
            data={tableRows}
            currentPage={listState.page}
            sorted={listState.sorted}
            filters={listState.filtered}
            loading={loading}
            enableMultiRowSelection={false}
            addRowSelection={false}
            defaultPageSize={listState.pageSize}
            attributeName={AI_CALL_LOG_PAGE_KEY}
            pageSizeOptions={[10, 15, 25, 30, 50]}
            onUpdateTableData={onUpdateTable}
            overrideRowId={AI_CALL_LOG_TABLE_ROW_KEY}
            dataTestId="ai-call-log"
            tdStyle={callLogTdStyle}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default AiCallLogList;
